import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Box, Tooltip, TooltipLink, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { NumbersPath } from 'entities/ExportRouters'
import { AddNumberCard } from 'pages/settings/pages/numbers/ui/AddNumberCard/AddNumberCard'
import { type IAddNumberType } from 'pages/settings/store/newInboxSteps/newInboxCommonStepsStore'
import styles from './styles.module.scss'

type IHowAddNumberCardsProps = {
  setAddNumberType: (addNumberType: IAddNumberType) => void
  nextClickHandler?: () => void
  unassignedNumbersCount: number
  isGracePeriod?: boolean
  hiddenKeys?: string[]
  isModalMode?: boolean
  selectedType?: IAddNumberType
}

export const HowAddNumberCards = observer(
  ({
    setAddNumberType,
    nextClickHandler,
    unassignedNumbersCount,
    isGracePeriod,
    hiddenKeys,
    isModalMode = false,
    selectedType,
  }: IHowAddNumberCardsProps) => {
    const ADD_NUMBER_INFO = [
      {
        key: 'assign',
        icon: 'phoneTransfer' as const,
        title: 'Assign a number',
        subtitle: `Your workspace has ${unassignedNumbersCount} unassigned number${
          unassignedNumbersCount === 1 ? '' : 's'
        } available`,
        isDisabled: unassignedNumbersCount === 0,
        handleClick: () => {
          setAddNumberType('assign')
          nextClickHandler?.()
        },
      },
      {
        key: 'buy',
        icon: 'shoppingCart' as const,
        title: 'Buy a number',
        subtitle: 'Get a local, toll-free or international number',
        isDisabled: isGracePeriod,
        handleClick: () => {
          setAddNumberType('buy')
          nextClickHandler?.()
        },
      },
      {
        key: 'app',
        icon: 'plug' as const,
        title: 'Add a number from an app',
        subtitle: 'Connect a number from a calling app (Aircall, RingCentral, etc.)',
        isDisabled: isGracePeriod,
        handleClick: () => {
          setAddNumberType('app')
          nextClickHandler?.()
        },
      },
      {
        key: 'landline',
        icon: 'phone' as const,
        title: 'Text-enable a landline',
        isDisabled: isGracePeriod,
        subtitle: 'Send and receive text messages with your landline',
        handleClick: () => {
          setAddNumberType('landline')
          nextClickHandler?.()
        },
      },
    ]

    return (
      <div className={classNames(styles.content, { [styles.modalMode]: isModalMode })}>
        {ADD_NUMBER_INFO.filter((item) => !hiddenKeys?.includes(item.key)).map((item, index) => {
          const shouldShowTooltip = isGracePeriod && item.title !== 'Assign a number'
          return (
            <Tooltip
              key={index}
              type='description'
              label={
                <Box display='flex' flexDirection='column' gap={0.5}>
                  <Typography
                    variant={'body-md-regular'}
                    color={'var(--content-primary-primary-inverted)'}
                    ariaLabel={'Grace Period'}
                  >
                    Take care of unpaid licenses
                    <br /> before adding new numbers.
                  </Typography>
                </Box>
              }
              placement={'top'}
              width={233}
              fullWidth
              withoutPointer
              visibility={shouldShowTooltip ? 'visible' : 'hidden'}
              desc={
                <TooltipLink
                  text='Manage numbers'
                  onClick={() => {
                    uiStore.changeRoute({
                      path: `${NumbersPath.root}`,
                    })
                  }}
                />
              }
            >
              <AddNumberCard
                key={index}
                icon={item.icon}
                title={item.title}
                subtitle={item.subtitle}
                isDisabled={item.isDisabled}
                handleClick={item.handleClick}
                isSelected={selectedType === (item.key as IAddNumberType)}
              />
            </Tooltip>
          )
        })}
      </div>
    )
  }
)
